import { useRef, useEffect, Fragment } from 'react';
import { avoidBreakInside } from '@provider/print';
import { ExpandCollapseArrow } from '@ui/kit/icons';
import { hyphenatableWordInput } from '@utils/component-framework';
import { useToggleState } from '@utils/react';
import { usePageContext } from '../../../../context.js';
import { useOpgavekompleksetEntry } from '../../../../state/hook.entry.js';
import { useUpdateInputMutation } from '../../../../state/mutations/update-input.js';
import { wrapper, word, char, hyphenWrapper, hyphenateButton, editToggle, editToggleArrow } from './atom.editable.word.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function HyphenatableWordEditable(props) {
  const ref = useRef(null);
  const pageContext = usePageContext();
  const [editable, editActions] = useToggleState();
  const update = useUpdateInputMutation();
  const inputEntry = useOpgavekompleksetEntry({
    select: entry => hyphenatableWordInput.entry.asType(entry?.data.tasks[props.taskEntryKey]?.details[props.node.id])
  });
  const inputHyphenation = inputEntry?.hyphenation ?? [props.node.text];
  const onChange = index => {
    update(props.taskEntryKey, props.node.id, prev => {
      const splittedAtIndices = getIndicesWordHasBeenSplittedAt(prev?.hyphenation ?? []);

      //
      // STEP: adjust our hyphens array to take the modification into account
      //
      const hasToggledHyphenAtIndex = splittedAtIndices.indexOf(index);
      if (hasToggledHyphenAtIndex === -1) {
        splittedAtIndices.push(index);
        splittedAtIndices.sort((a, b) => a - b);
      } else {
        splittedAtIndices.splice(hasToggledHyphenAtIndex, 1);
      }
      splittedAtIndices.unshift(-1);

      //
      // STEP: Convert our index array back into the expected stringified
      // structure.
      //
      const hyphenation = splittedAtIndices.map((value, index) => props.node.text.slice(value + 1, (splittedAtIndices[index + 1] ?? props.node.text.length) + 1));
      return {
        type: "hyphenatable-word",
        hyphenation
      };
    });
  };
  const hasToggledHyphenAtIndices = getIndicesWordHasBeenSplittedAt(inputHyphenation);
  useEffect(() => {
    const target = ref.current;
    const onClick = evt => {
      if (evt.target instanceof HTMLElement && (target?.contains(evt.target) || target === evt.target)) {
        return;
      }
      editActions.disable();
    };
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("click", onClick);
    };
  }, [editActions]);
  return /*#__PURE__*/jsx("div", {
    className: wrapper,
    children: /*#__PURE__*/jsxs("div", {
      ref: ref,
      className: word,
      "data-page-context": pageContext,
      "data-editable": editable,
      ...avoidBreakInside(),
      onClick: editActions.enable,
      children: [[...props.node.text].map((char$1, index) => /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx("span", {
          className: char,
          children: char$1
        }), index !== props.node.text.length - 1 && /*#__PURE__*/jsx("div", {
          className: hyphenWrapper,
          "data-toggled": hasToggledHyphenAtIndices.includes(index),
          "data-editable": editable,
          children: /*#__PURE__*/jsx("button", {
            className: hyphenateButton,
            "aria-label": hasToggledHyphenAtIndices.includes(index) ? "Fjern denne bindestreg" : "Tilføj bindestreg her",
            onClick: () => onChange(index),
            inert: !editable
          })
        })]
      }, index)), /*#__PURE__*/jsxs("button", {
        className: editToggle,
        onClick: evt => {
          evt.stopPropagation();
          editActions.toggle();
        },
        "aria-label": editable ? "Luk redigeringstilstand" : "Åbn redigeringstilstand",
        children: [/*#__PURE__*/jsx(ExpandCollapseArrow, {
          className: editToggleArrow
        }), /*#__PURE__*/jsx(ExpandCollapseArrow, {
          className: editToggleArrow
        })]
      })]
    })
  });
}

/**
 * converts our data structure of the form ["ord", "et"] to an array of
 * indicies wherein hyphens should be inserted.
 *
 * @example
 * ["or", "d", "et"] -> [1, 2]
 */
function getIndicesWordHasBeenSplittedAt(hyphenation) {
  return hyphenation.slice(0, -1).reduce((acc, curr) => {
    const prev = acc.at(-1) ?? 0;
    const next = prev + curr.length;
    acc.push(next);
    return acc;
  }, []).map(it => it - 1);
}

export { HyphenatableWordEditable };
//# sourceMappingURL=atom.editable.word.js.map
